/**
 * Parser for bloqueios files
 */

import { readFile } from '../shared/fileReader';
import { normalizeDate } from '../shared/dateNormalizer';
import { validateBloqueiosHeaders, generateBloqueiosHeaderError } from '../../validations/bloqueios/validator';
import { excelDateToJSDate } from '../../dateUtils';

/**
 * Normalizes a date value for bloqueios with better Brazilian format support
 * @param {number|string} dateValue - Date value to normalize
 * @returns {Date} Normalized Date object
 */
function normalizeBloqueiosDate(dateValue) {
  // If it's a number (Excel date), use the Excel converter
  if (typeof dateValue === 'number') {
    return excelDateToJSDate(dateValue);
  }
  
  // If it's a string, try various formats
  if (typeof dateValue === 'string') {
    const cleanDate = dateValue.trim();
    
    // Try Brazilian format dd/mm/yyyy first
    const brazilianMatch = cleanDate.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (brazilianMatch) {
      const day = parseInt(brazilianMatch[1], 10);
      const month = parseInt(brazilianMatch[2], 10);
      const year = parseInt(brazilianMatch[3], 10);
      
      // Create date in ISO format (yyyy-mm-dd) to avoid locale issues
      const isoDate = new Date(year, month - 1, day);
      if (!isNaN(isoDate.getTime())) {
        return isoDate;
      }
    }
    
    // Try ISO format yyyy-mm-dd
    const isoMatch = cleanDate.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
    if (isoMatch) {
      const year = parseInt(isoMatch[1], 10);
      const month = parseInt(isoMatch[2], 10);
      const day = parseInt(isoMatch[3], 10);
      
      const isoDate = new Date(year, month - 1, day);
      if (!isNaN(isoDate.getTime())) {
        return isoDate;
      }
    }
    
    // Fallback to native Date parsing
    const fallbackDate = new Date(cleanDate);
    if (!isNaN(fallbackDate.getTime())) {
      return fallbackDate;
    }
  }
  
  throw new Error('Invalid time value');
}

/**
 * Validates and processes bloqueios data
 * @param {Array} rows - Raw rows from file
 * @param {Array} headers - Column headers
 * @returns {Array} Processed bloqueios data
 */
function processBloqueiosData(rows, headers) {
  if (!rows || rows.length === 0) {
    throw new Error('Arquivo vazio ou sem dados válidos.');
  }

  // Validate required headers
  const headerValidation = validateBloqueiosHeaders(headers);
  if (!headerValidation.valid) {
    const errorMessage = generateBloqueiosHeaderError(headerValidation, headers);
    throw new Error(errorMessage);
  }
  
  // Log warnings if any
  if (headerValidation.warnings.length > 0) {
    console.warn('⚠️ Avisos na validação de bloqueios:', headerValidation.warnings);
  }

  const processedData = [];
  const errors = [];

  for (let i = 0; i < rows.length; i++) {
    const row = rows[i];
    const rowNumber = i + 2; // +2 porque começamos do índice 0 e há o cabeçalho

    try {
      // Extract required fields
      const id = row['ID'];
      const dataCriacao = row['Data de Criação'];
      const dataConclusao = row['Data de Conclusão'];
      
      // Se a coluna Categoria existir, validar que não está vazia
      let categoria;
      if (headers.includes('Categoria')) {
        const categoriaValue = row['Categoria']?.trim();
        if (!categoriaValue) {
          errors.push(`Linha ${rowNumber}: Categoria é obrigatória quando a coluna existe. Preencha a categoria ou remova a coluna do arquivo.`);
          continue;
        }
        categoria = categoriaValue;
      } else {
        categoria = 'Padrão';
      }

      // Validate required fields
      if (!id) {
        errors.push(`Linha ${rowNumber}: ID é obrigatório`);
        continue;
      }

      if (!dataCriacao) {
        errors.push(`Linha ${rowNumber}: Data de Criação é obrigatória`);
        continue;
      }

      if (!dataConclusao) {
        errors.push(`Linha ${rowNumber}: Data de Conclusão é obrigatória`);
        continue;
      }

      // Process dates with improved handling
      let criacaoDate, conclusaoDate;

      try {
        // Handle Excel date numbers and string dates with better Brazilian format support
        criacaoDate = normalizeBloqueiosDate(dataCriacao);
        conclusaoDate = normalizeBloqueiosDate(dataConclusao);
      } catch (dateError) {
        errors.push(`Linha ${rowNumber}: ${dateError.message}`);
        continue;
      }

      // Validate date logic
      if (conclusaoDate < criacaoDate) {
        errors.push(`Linha ${rowNumber}: Data de Conclusão deve ser posterior à Data de Criação`);
        continue;
      }

      // Create processed item
      const processedItem = {
        'ID': id,
        'Data de Criação': criacaoDate.toISOString().split('T')[0],
        'Data de Conclusão': conclusaoDate.toISOString().split('T')[0],
        'Categoria': categoria.trim()
      };

      processedData.push(processedItem);

    } catch (error) {
      errors.push(`Linha ${rowNumber}: ${error.message}`);
    }
  }

  // Report errors if any
  if (errors.length > 0) {
    const errorMessage = `Encontrados ${errors.length} erro(s) no arquivo:\n\n${errors.slice(0, 10).join('\n')}${errors.length > 10 ? `\n\n...e mais ${errors.length - 10} erro(s)` : ''}`;
    throw new Error(errorMessage);
  }

  if (processedData.length === 0) {
    throw new Error('Nenhum dado válido foi encontrado no arquivo.');
  }

  return processedData;
}

/**
 * Parse bloqueios file (CSV or XLSX)
 * @param {File} file - File to parse
 * @returns {Promise<Array>} Processed bloqueios data
 */
export async function parseBloqueiosFile(file) {
  try {
    const { headers, rawData } = await readFile(file);
    return processBloqueiosData(rawData, headers);
  } catch (error) {
    // Re-throw with more context
    throw new Error(`Erro ao processar arquivo de bloqueios: ${error.message}`);
  }
}
